import { Divider, Modal, Spin, Table, Tabs, Tag } from "antd";
import { useEffect, useState } from "react";

import { regionApi } from "../../../../../../Services";

interface Props {
  region: { id?: string; regionName?: string } | null;
  onClose: () => void;
}

/**
 * Region drill-down (v2.1): GET /region/{id}/drilldown, shown as the region's
 * Categories, Sub-Regions and Assigned Vendors. Opened by clicking a region.
 */
export const RegionDrilldownModal = ({ region, onClose }: Props) => {
  const [data, setData] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!region?.id) return;
    let current = true;
    setIsLoading(true);
    setData(null);
    regionApi
      .drilldown(region.id)
      .then((result: any) => current && setData(result))
      .catch(() => current && setData({}))
      .finally(() => current && setIsLoading(false));
    return () => {
      current = false;
    };
  }, [region?.id]);

  const categories: any[] = Array.isArray(data?.categories) ? data.categories : [];
  /* Sub-regions arrive as plain names. */
  const subRegions: any[] = (Array.isArray(data?.subRegions) ? data.subRegions : []).map((entry: any) =>
    typeof entry === "string" ? { subRegionName: entry } : entry
  );
  const vendors: any[] = Array.isArray(data?.vendors) ? data.vendors : [];

  return (
    <Modal
      title={data?.regionName ?? region?.regionName ?? "Region"}
      open={!!region}
      onCancel={onClose}
      footer={null}
      centered
      width={760}
      destroyOnClose
    >
      <Divider style={{ margin: "-20px 0 12px", borderTop: "1px solid #d9d9d9" }} />
      <Spin spinning={isLoading}>
        <Tabs
          items={[
            {
              key: "categories",
              label: `Categories (${categories.length})`,
              children: (
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(row: any) => String(row.id ?? row.categoryName)}
                  dataSource={categories}
                  columns={[
                    { title: "Category Name", dataIndex: "categoryName" },
                    { title: "Category Code", dataIndex: "categoryCode", render: (value) => value || "-" },
                  ]}
                />
              ),
            },
            {
              key: "subRegions",
              label: `Sub-Regions (${subRegions.length})`,
              children: (
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(row: any) => String(row.id ?? row.subRegionName)}
                  dataSource={subRegions}
                  columns={[{ title: "Sub Region", dataIndex: "subRegionName" }]}
                />
              ),
            },
            {
              key: "vendors",
              label: `Assigned Vendors (${vendors.length})`,
              children: (
                <Table
                  size="small"
                  pagination={false}
                  rowKey={(row: any) => String(row.id ?? row.vendorName)}
                  dataSource={vendors}
                  columns={[
                    { title: "Vendor Name", dataIndex: "vendorName" },
                    {
                      title: "Status",
                      dataIndex: "isActive",
                      render: (value, row: any) =>
                        value === false || row.active === false ? <Tag>Inactive</Tag> : <Tag color="green">Active</Tag>,
                    },
                    { title: "Active Contracts", dataIndex: "activeContractCount", render: (value) => value ?? "-" },
                    {
                      title: "Total Contracts",
                      dataIndex: "totalContractCount",
                      render: (value, row: any) => value ?? row.contractsAssigned ?? "-",
                    },
                  ]}
                />
              ),
            },
          ]}
        />
      </Spin>
    </Modal>
  );
};
